import React, { useState, useEffect } from "react";
import styled from "styled-components";

// Utility function to get higher resolution image URLs
const getHighResImageUrl = (originalUrl) => {
    if (!originalUrl) return originalUrl;
    
    // For Shopify images, replace size suffixes with larger versions
    if (originalUrl.includes('cdn.shopify.com')) {
        // Replace _medium, _small, _compact, _thumb with _1024x1024 for better quality
        return originalUrl
            .replace(/_medium\.jpg/gi, '_1024x1024.jpg')
            .replace(/_small\.jpg/gi, '_1024x1024.jpg')
            .replace(/_compact\.jpg/gi, '_1024x1024.jpg')
            .replace(/_thumb\.jpg/gi, '_1024x1024.jpg')
            .replace(/_medium\.png/gi, '_1024x1024.png')
            .replace(/_small\.png/gi, '_1024x1024.png')
            .replace(/_compact\.png/gi, '_1024x1024.png')
            .replace(/_thumb\.png/gi, '_1024x1024.png');
    }
    
    // For Google Cloud Storage and other sources, return original URL
    return originalUrl;
};

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    cursor: zoom-out;
`;

const ModalContent = styled.div`
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const ZoomedImage = styled.img`
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    max-width: 600px;
    max-height: 600px;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    cursor: ${props => props.isZoomed ? 'zoom-out' : 'zoom-in'};
    transform: scale(${props => props.isZoomed ? '1.5' : '1'});
    transition: transform 0.3s ease, opacity 0.3s ease;
    opacity: ${props => props.isLoaded ? 1 : 0};
    
    /* High-quality image rendering */
    image-rendering: -webkit-optimize-contrast;
    image-rendering: optimize-quality;
    -ms-interpolation-mode: bicubic;
    
    /* Prevent blurring on transform */
    transform-origin: center center;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    will-change: transform;
`;

const LoadingSpinner = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top: 4px solid white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    display: ${props => props.show ? 'block' : 'none'};

    @keyframes spin {
        0% { transform: translate(-50%, -50%) rotate(0deg); }
        100% { transform: translate(-50%, -50%) rotate(360deg); }
    }
`;

const LoadingContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
    min-width: 300px;
`;

const CloseButton = styled.button`
    position: absolute;
    top: -40px;
    right: 0;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    font-size: 20px;
    font-weight: bold;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

    &:hover {
        background: white;
        transform: scale(1.1);
    }
`;

const ImageInfo = styled.div`
    position: absolute;
    bottom: -50px;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 10px;
    border-radius: 4px;
    text-align: center;
    font-size: 14px;
`;

const ZoomInstructions = styled.div`
    position: absolute;
    bottom: -120px;
    left: 0;
    right: 0;
    color: rgba(255, 255, 255, 0.8);
    text-align: center;
    font-size: 12px;
`;

function ImageZoomModal({ isOpen, imageUrl, imageAlt, imageInfo, onClose }) {
    const [isZoomed, setIsZoomed] = useState(false);
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    useEffect(() => {
        if (isOpen) {
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
            
            // Reset zoom and loading state when opening
            setIsZoomed(false);
            setIsImageLoaded(false);
            
            // Add escape key listener
            const handleEscape = (e) => {
                if (e.key === 'Escape') {
                    onClose();
                }
            };
            
            document.addEventListener('keydown', handleEscape);
            
            return () => {
                document.body.style.overflow = 'unset';
                document.removeEventListener('keydown', handleEscape);
            };
        }
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleImageClick = (e) => {
        e.stopPropagation();
        setIsZoomed(!isZoomed);
    };

    const handleImageLoad = () => {
        setIsImageLoaded(true);
    };

    const handleImageError = () => {
        setIsImageLoaded(true); // Still hide the spinner even if image fails to load
    };

    return (
        <ModalOverlay onClick={handleOverlayClick}>
            <ModalContent>
                <CloseButton onClick={onClose} title="Close (Esc)">
                    ×
                </CloseButton>
                
                <LoadingContainer>
                    <LoadingSpinner show={!isImageLoaded} />
                    
                    <ZoomedImage
                        src={getHighResImageUrl(imageUrl)}
                        alt={imageAlt}
                        isZoomed={isZoomed}
                        isLoaded={isImageLoaded}
                        onClick={handleImageClick}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                        title={isZoomed ? "Click to zoom out" : "Click to zoom in"}
                    />
                </LoadingContainer>
                
                {imageInfo && (
                    <ImageInfo>
                        {imageInfo}
                    </ImageInfo>
                )}
                
                <ZoomInstructions>
                    Click image to {isZoomed ? 'zoom out' : 'zoom in'} • Click outside or press Esc to close
                </ZoomInstructions>
            </ModalContent>
        </ModalOverlay>
    );
}

export default ImageZoomModal; 